import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useProjects } from "../../context/ProjectsContext";
import { useAuth } from "../../context/AuthContext";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { isSuperAdmin } from "../../utils/roles";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ProjectBillingGate({ children, featureName = "esta función" }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject } = useProjects();
  const superAdmin = isSuperAdmin(user);

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, activeProject, projectId]);

  const access = getProjectBillingAccess(project);
  // El superadmin necesita acceso técnico aun cuando el proyecto esté vencido,
  // suspendido por billing o en modo solo landing.
  if (superAdmin || !project || access.isOperational) return children;

  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const settingsPath = `/dashboard/proyectos/${projectId}/configuracion/general`;
  const landingPath = `/dashboard/proyectos/${projectId}/pagina`;
  const billingRestricted = access.isLandingOnly;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 5,
        border: "1px solid rgba(25,36,47,.09)",
        overflow: "hidden",
        boxShadow: "0 22px 55px rgba(25,36,47,.08)",
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 5 },
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#30485B 100%)",
        }}
      >
        <Stack spacing={2.5} alignItems="flex-start">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.18)",
              color: "#FF8B72",
            }}
          >
            <LockClockRoundedIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "rgba(255,255,255,.62)" }}>
              {billingRestricted ? "PAGO PENDIENTE" : "PROYECTO SUSPENDIDO"}
            </Typography>
            <Typography variant="h3" sx={{ mt: 0.3, fontWeight: 950, letterSpacing: "-.04em" }}>
              {featureName} está temporalmente pausada
            </Typography>
            <Typography sx={{ mt: 1.2, maxWidth: 760, color: "rgba(255,255,255,.76)" }}>
              {billingRestricted
                ? "No borramos nada. La información del proyecto está segura y la landing continúa disponible. Al regularizar el pago, WebProfit vuelve a habilitar todo automáticamente."
                : "El proyecto fue suspendido administrativamente. Los datos permanecen guardados hasta que un administrador vuelva a habilitarlo."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              startIcon={<CreditCardRoundedIcon />}
              onClick={() => navigate(billingRestricted ? billingPath : settingsPath)}
              sx={{ bgcolor: BRAND.orange, borderRadius: 999, px: 3, textTransform: "none", fontWeight: 950 }}
            >
              {billingRestricted ? "Regularizar y reactivar" : "Ver configuración"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PublicRoundedIcon />}
              onClick={() => navigate(landingPath)}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,.35)", borderRadius: 999, px: 3, textTransform: "none", fontWeight: 900 }}
            >
              Ir a la landing
            </Button>
          </Stack>
        </Stack>
      </Box>
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: "#fff" }}>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          {billingRestricted
            ? <>El pago de WebProfit es independiente de las ventas y cobros que realiza tu comercio. Podés cambiar la tarjeta o verificar un pago desde <strong>Tu plan</strong>.</>
            : <>La suspensión administrativa no elimina información. Revisá el estado general del proyecto para volver a habilitarlo.</>}
        </Alert>
      </CardContent>
    </Card>
  );
}
